import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import jsonToFormData from "json-form-data";
import { BASE_URL } from "./config";
import LoginService from "./login.service";
import { getToken } from "./auth";

const isDev = process.env.NODE_ENV === "development";

const api = axios.create({
	baseURL: `${BASE_URL}/`,
	timeout: 30000,
});

// Request interceptor — injeta token e converte FormData
api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
	const token = getToken();

	if (token) {
		config.headers.Authorization = `Bearer ${token}`;
	}
	config.withCredentials = true;

	if (config.data && config.headers.get("Content-Type") === "multipart/form-data") {
		const options = {
			initialFormData: new FormData(),
			showLeafArrayIndexes: true,
			includeNullValues: false,
		};
		config.data = jsonToFormData(config.data, options);
	}

	return config;
});

// Lock para evitar multiplos refresh simultaneos
let refreshPromise: Promise<unknown> | null = null;

// Response interceptor — trata 401/403 com refresh
api.interceptors.response.use(
	(response) => response,
	async (error: AxiosError) => {
		const originalReq = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

		if (!originalReq || originalReq._retry) {
			return Promise.reject(error);
		}

		// Erros que nao sao de auth — rejeita direto
		if (
			!error.response ||
			(error.response.status !== 401 && error.response.status !== 403) ||
			originalReq.url === "token/"
		) {
			return Promise.reject(error);
		}

		// Refresh token falhou — logout
		if (originalReq.url === "token/refresh/") {
			localStorage.removeItem("token");
			localStorage.removeItem("refresh_token");
			localStorage.removeItem("user_info");
			if (window.location.pathname !== "/") {
				localStorage.setItem("redirectUrl", window.location.pathname);
			}
			window.location.href = `${window.location.origin}/`;
			return Promise.reject(error);
		}

		originalReq._retry = true;

		// Lock: se ja tem um refresh em andamento, aguarda o mesmo
		if (!refreshPromise) {
			const loginService = new LoginService();
			refreshPromise = loginService.refreshToken().finally(() => {
				refreshPromise = null;
			});
		}

		try {
			await refreshPromise;
			return api.request(originalReq);
		} catch (refreshError) {
			if (isDev) {
				// console.error("[api] Refresh token falhou:", refreshError);
			}
			return Promise.reject(refreshError);
		}
	}
);

export default api;
